import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Text,
  Flex,
  ModalFooter,
  Button,
  Input,
  Progress,
} from "@chakra-ui/react";
import { Preview } from "components/add-people";
import { getAccount, updateUser } from "utils/sessionHelper";
import { textStyle, variant } from "theme/names";
import { ContactListDropdown } from "components/contact-list-dropdown";
import { toast } from "react-hot-toast";
import { addRemovePeopleToList } from "controllers/list";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  people: any[];
  mode: "new" | "existing";
  refetchAll?: boolean;
}

export function AddToExistingListPopup({
  isOpen,
  onClose,
  people,
  mode,
  refetchAll = false,
}: Props) {
  const [listName, setListName] = useState<string>("");
  const [selectedList, setSelectedList] = useState<any>({});
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [hasSet, setHasSet] = useState<boolean>(false);
  const [loaderData, setDataLoaderAdd] = useState<any>({});
  const { data: session } = useSession() ?? {};

  useEffect(() => {
    if (session && !hasSet) {
      setHasSet(true);
      const contactLists = getAccount(session)?.contactLists;
      setSelectedList(contactLists?.[0]);
    }
  }, [session]);

  const handleAdd = async () => {
    try {
      setIsAdding(true);
      let listId = selectedList.id;

      if (mode === "new") {
        listId =
          getAccount(session).contactLists &&
            getAccount(session).contactLists?.length > 0
            ? Math.max(...getAccount(session).contactLists.map((x) => x.id)) + 1
            : 0;

        let contactLists = getAccount(session)?.contactLists ?? [];
        // @ts-ignore
        contactLists.push({
          name: listName,
          id: listId,
          createdAt: new Date(),
        });

        await updateUser(session, { contactLists });
      }

      await addRemovePeopleToList(
        getAccount(session)?.id,
        // @ts-ignore
        listId,
        people,
        "save",
        setDataLoaderAdd,
        refetchAll,
      );
      toast.success("Successfully added people to the list");
      setListName("");
      onClose();
    } catch (e) {
      console.error("Error in adding people to list: ", e);
      toast.error("Error in adding people to list: " + e.message);
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Add to {mode === "new" ? "new" : "existing"} contacts list
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box>
            <Preview
              people={people}
              align="start"
              noOfImagePreview={5}
              mainContainerProps={{ pl: 0 }}
            />
          </Box>
          <Flex alignItems="center" mt="4">
            {mode === "new" ? (
              <Flex justifyContent="space-between">
                <Input
                  value={listName}
                  placeholder="List name"
                  maxWidth="65%"
                  onChange={(e) => setListName(e.target.value)}
                  isDisabled={isAdding}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleAdd();
                    }
                  }}
                />
                <Button
                  variant={"primary"}
                  onClick={handleAdd}
                  isLoading={isAdding}
                  ml={4}
                >
                  Create & Add
                </Button>
              </Flex>
            ) : (
              <>
                <Text
                  mr={4}
                  textStyle={textStyle["body.medium.light"]}
                  fontWeight="medium"
                >
                  Select list:{" "}
                </Text>
                <ContactListDropdown
                  onSelectList={(list) => setSelectedList(list)}
                  value={selectedList}
                />
              </>
            )}
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent="center" flexDir="column">
          {loaderData?.total > 0 && isAdding && (
            <Box w="100%" h="40px" mt="5">
              <Progress
                size="xs"
                rounded="full"
                value={Math.floor((loaderData?.done / loaderData?.total) * 100)}
              />
            </Box>
          )}
          {mode === "existing" ? (
            <Flex justifyContent="flex-end" w="100%">
              <Button
                onClick={onClose}
                mr="6"
                isDisabled={isAdding}
                variant="tertiary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAdd}
                isLoading={isAdding}
                variant={variant.Button.primary}
              >
                Add
              </Button>
            </Flex>
          ) : null}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
